import React, { useState, useEffect, useCallback, useRef } from 'react'
import Swal from 'sweetalert2'
import { FiSearch, FiX, FiMail, FiAlertTriangle, FiCalendar } from 'react-icons/fi'
import { MdClose } from 'react-icons/md'
import EmailFrame from './EmailFrame'

const formatTime = (value) => new Date(value).toLocaleString(undefined, {
  month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
})

const STATUS_FILTERS = [
  ['', 'All'], ['delivered', 'Delivered'], ['opened', 'Opened'], ['pending', 'Not confirmed'],
  ['problems', 'Bounced / spam'], ['failed', 'Failed'],
]

// shown as plain coloured text in the table
export const STATUS_LABELS = {
  sent: 'Sent', delivery_delayed: 'Delayed', delivered: 'Delivered',
  bounced: 'Bounced', suppressed: 'Suppressed', complained: 'Marked spam', failed: 'Failed',
}
const STATUS_TONE = {
  sent: 'muted', delivery_delayed: 'amber', delivered: 'green',
  bounced: 'red', suppressed: 'red', complained: 'red', failed: 'red',
}
const STATUS_HELP = {
  sent: 'Resend accepted it. Delivery not confirmed yet.',
  delivery_delayed: 'The recipient\'s server is slow to accept it. Resend keeps trying.',
  delivered: 'The recipient\'s mail server accepted it.',
  bounced: 'The recipient\'s mail server rejected it. Check the address.',
  suppressed: 'Resend didn\'t send it because this address bounced or complained before.',
  complained: 'The recipient marked it as spam. Resend will stop sending to them.',
  failed: 'Resend refused the request, so nothing was sent.',
}
const EVENT_LABELS = {
  delivered: 'Delivered', delivery_delayed: 'Delivery delayed', bounced: 'Bounced', suppressed: 'Suppressed',
  complained: 'Marked as spam', failed: 'Failed', opened: 'Opened', clicked: 'Link clicked',
}
const KIND_LABELS = { system: 'Automatic', manual: 'Written in dashboard', reply: 'Reply from inbox' }

// confirmation used by every email delete
export const confirmDelete = async (count, where = 'from the dashboard') => (await Swal.fire({
  title: count === 1 ? 'Delete this email?' : `Delete ${count} emails?`,
  text: `${count === 1 ? 'It is' : 'They are'} removed ${where} for good.`,
  icon: 'warning', showCancelButton: true, confirmButtonText: 'Delete', confirmButtonColor: 'rgb(220,70,70)',
  background: 'rgb(8,14,30)', color: '#fff',
})).isConfirmed

export const toast = (icon, title) => Swal.fire({ toast: true, position: 'top-end', icon, title, showConfirmButton: false, timer: 3000, timerProgressBar: true })

// "N selected · Delete selected · Clear", shown above a list while rows are ticked
export const SelectionBar = ({ count, busy, onDelete, onClear }) => (count > 0 ? (
  <div className="adm-selection-bar">
    <span><strong>{count}</strong> selected</span>
    <button className="adm-btn danger small" disabled={busy} onClick={onDelete}>{busy ? 'Deleting…' : 'Delete selected'}</button>
    <button className="adm-link-btn" onClick={onClear}>Clear</button>
  </div>
) : null)

export const StatusText = ({ status }) => (
  <span className={`adm-email-status ${STATUS_TONE[status] || 'muted'}`}>{STATUS_LABELS[status] || status}</span>
)

// One sent email: details, delivery history, preview and resend
const SentEmailModal = ({ id, route, adminFetch, onClose, onResent, onDeleted }) => {
  const [data, setData] = useState(null)
  const [error, setError] = useState('')
  const [resending, setResending] = useState(false)

  useEffect(() => {
    (async () => {
      try {
        const res = await (await adminFetch(`${route}/api/admin/emailLogs/${id}`)).json()
        if (res.status !== 200) throw new Error(res.message)
        setData(res)
      } catch (e) {
        setError(e.message || 'Could not load the email')
      }
    })()
  }, [id]) // eslint-disable-line react-hooks/exhaustive-deps

  const resend = async () => {
    setResending(true)
    try {
      const res = await (await adminFetch(`${route}/api/admin/emailLogs/${id}/resend`, { method: 'POST' })).json()
      if (res.status !== 200) throw new Error(res.message)
      toast('success', 'Email sent again')
      onResent()
      onClose()
    } catch (e) {
      toast('error', e.message || 'Not sent')
    } finally {
      setResending(false)
    }
  }

  const remove = async () => {
    if (!(await confirmDelete(1, 'from the dashboard (Resend keeps its own record)'))) return
    const res = await (await adminFetch(`${route}/api/admin/emailLogs/${id}`, { method: 'DELETE' })).json()
    if (res.status !== 200) return toast('error', res.message || 'Could not delete')
    toast('success', 'Email deleted')
    onDeleted()
    onClose()
  }

  const log = data?.log

  return (
    <div className="adm-modal-overlay" onClick={onClose}>
      <div className="adm-modal xwide scroll" onClick={e => e.stopPropagation()}>
        <button className="adm-modal-close" onClick={onClose} aria-label="Close"><MdClose size={16} /></button>
        {!log && <p className="adm-modal-sub">{error || 'Loading…'}</p>}
        {log && (
          <>
            <p className="adm-modal-title">{log.subject || '(no subject)'}</p>
            <p className="adm-modal-sub">To <strong>{log.to}</strong></p>

            <div className="adm-detail-row"><span>Status</span><strong><StatusText status={log.status} /></strong></div>
            <p className="adm-email-help">{STATUS_HELP[log.status]}</p>
            {log.error && <div className="adm-email-error">{log.error}</div>}
            <div className="adm-detail-row"><span>Sent</span><strong>{formatTime(log.createdAt)}</strong></div>
            {log.from && <div className="adm-detail-row"><span>From</span><strong>{log.from}</strong></div>}
            <div className="adm-detail-row"><span>Type</span><strong>{log.type} · {KIND_LABELS[log.kind] || 'Automatic'}</strong></div>
            {log.sentBy && <div className="adm-detail-row"><span>Sent by</span><strong>{log.sentBy}</strong></div>}
            {log.openedAt && <div className="adm-detail-row"><span>First opened</span><strong>{formatTime(log.openedAt)}</strong></div>}
            {log.clickedAt && <div className="adm-detail-row"><span>First click</span><strong>{formatTime(log.clickedAt)}</strong></div>}

            {log.events?.length > 0 && (
              <>
                <p className="adm-detail-heading">History</p>
                <ul className="adm-email-timeline">
                  <li><span>{formatTime(log.createdAt)}</span>Sent to Resend</li>
                  {log.events.map((event, i) => (
                    <li key={i}>
                      <span>{formatTime(event.at)}</span>
                      {EVENT_LABELS[event.type] || event.type}
                      {event.detail && <em>{event.detail}</em>}
                    </li>
                  ))}
                </ul>
              </>
            )}

            <p className="adm-detail-heading">Email</p>
            {data.html
              ? <EmailFrame html={data.html} />
              : <p className="adm-muted-note">No copy was kept of this email (it was sent before previews were added).</p>}
            {log.content && !log.resendable && (
              <p className="adm-email-help">The one-time link in this email was removed from the copy for security, so it can't be resent.</p>
            )}

            <div className="adm-modal-actions">
              <button className="adm-btn danger" onClick={remove}>Delete</button>
              <button className="adm-btn ghost" onClick={onClose}>Close</button>
              {log.resendable && log.content && (
                <button className="adm-btn primary" disabled={resending} onClick={resend}>
                  {resending ? 'Sending…' : 'Send again'}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

// "Sent" tab: every email the backend has tried to send, newest first.
// `adminFetch` is the dashboard's authenticated fetch (adds the admin token).
const EmailLogPanel = ({ route, adminFetch, refreshKey, onChange }) => {
  const [logs, setLogs] = useState([])
  const [types, setTypes] = useState([])
  const [counts, setCounts] = useState({ problemsThisWeek: 0 })
  const [quota, setQuota] = useState(null)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [openId, setOpenId] = useState(null)
  const [selected, setSelected] = useState(() => new Set())
  const [deleting, setDeleting] = useState(false)

  const [search, setSearch] = useState('')
  const [query, setQuery] = useState('')      // debounced copy of `search`
  const [status, setStatus] = useState('')
  const [type, setType] = useState('')

  // the dashboard re-creates adminFetch on every render; keep the latest in a ref so it
  // isn't a dependency of load() (which would refetch in an endless loop)
  const fetchRef = useRef(adminFetch)
  fetchRef.current = adminFetch

  // wait for typing to pause before searching
  useEffect(() => {
    const timer = setTimeout(() => setQuery(search.trim()), 300)
    return () => clearTimeout(timer)
  }, [search])

  const load = useCallback(async (before) => {
    setLoading(true)
    setError('')
    try {
      const params = new URLSearchParams({ search: query, status, type })
      if (before) params.set('before', before)
      const res = await (await fetchRef.current(`${route}/api/admin/emailLogs?${params}`)).json()
      if (res.status !== 200) throw new Error(res.message || 'Could not load the email log')
      setLogs(prev => (before ? [...prev, ...res.logs] : res.logs))
      if (!before) setSelected(new Set())
      setHasMore(res.hasMore)
      setTypes(res.types)
      setCounts(res.counts)
      setQuota(res.quota)
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [route, query, status, type])

  // reload from the top whenever a filter changes, or after an email is sent elsewhere
  useEffect(() => { load() }, [load, refreshKey])

  const filtersActive = query || status || type

  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev)
    next.has(id) ? next.delete(id) : next.add(id)
    return next
  })
  const allSelected = logs.length > 0 && logs.every(l => selected.has(l._id))
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(logs.map(l => l._id)))

  const deleteSelected = async () => {
    if (!(await confirmDelete(selected.size, 'from the dashboard (Resend keeps its own record)'))) return
    setDeleting(true)
    try {
      const res = await (await fetchRef.current(`${route}/api/admin/emailLogs/delete`, {
        method: 'POST', body: JSON.stringify({ ids: [...selected] }),
      })).json()
      if (res.status !== 200) throw new Error(res.message)
      toast('success', `${res.deleted} email${res.deleted === 1 ? '' : 's'} deleted`)
      load()
      onChange?.()
    } catch (e) {
      toast('error', e.message || 'Could not delete')
    } finally {
      setDeleting(false)
    }
  }

  return (
    <>
      <div className="adm-stats-row three">
        <div className="adm-stat-card">
          <span className="adm-stat-icon teal"><FiMail /></span>
          <span className="adm-stat-text">
            <span>Sent today</span>
            <strong>{quota ? `${quota.today} / ${quota.dailyLimit}` : '—'}</strong>
          </span>
        </div>
        <div className="adm-stat-card">
          <span className="adm-stat-icon blue"><FiCalendar /></span>
          <span className="adm-stat-text">
            <span>This month</span>
            <strong>{quota ? `${quota.month.toLocaleString()} / ${quota.monthlyLimit.toLocaleString()}` : '—'}</strong>
          </span>
        </div>
        <button className={`adm-stat-card ${status === 'problems' ? 'active' : ''}`} onClick={() => setStatus(status === 'problems' ? '' : 'problems')}>
          <span className="adm-stat-icon amber"><FiAlertTriangle /></span>
          <span className="adm-stat-text"><span>Problems (7 days)</span><strong>{counts.problemsThisWeek}</strong></span>
        </button>
      </div>

      <div className="adm-toolbar">
        <div className="adm-search">
          <FiSearch className="adm-search-icon" />
          <input
            type="search"
            placeholder="Search by recipient or subject…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && (
            <button className="adm-search-clear" onClick={() => setSearch('')} aria-label="Clear search"><FiX size={14} /></button>
          )}
        </div>
        <select className="adm-sort" value={type} onChange={e => setType(e.target.value)} aria-label="Filter by email type">
          <option value="">All email types</option>
          {types.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
        </select>
      </div>

      <div className="adm-chips">
        {STATUS_FILTERS.map(([value, label]) => (
          <button key={label} className={`adm-chip ${status === value ? 'active' : ''}`} onClick={() => setStatus(value)}>
            {label}
          </button>
        ))}
      </div>

      <p className="adm-result-count">
        {loading && logs.length === 0 ? 'Loading…' : `Showing ${logs.length}${hasMore ? '+' : ''} email${logs.length === 1 ? '' : 's'}`}
        {filtersActive && (
          <button className="adm-link-btn" onClick={() => { setSearch(''); setStatus(''); setType('') }}>Clear filters</button>
        )}
      </p>

      {error && <div className="adm-empty"><p>{error}</p></div>}

      <SelectionBar count={selected.size} busy={deleting} onDelete={deleteSelected} onClear={() => setSelected(new Set())} />

      {!error && logs.length > 0 && (
        <div className="adm-table-wrap">
          <table className="adm-table">
            <thead>
              <tr>
                <th className="adm-check-cell">
                  <input type="checkbox" checked={allSelected} onChange={toggleAll} aria-label="Select all shown" />
                </th>
                <th>Sent</th>
                <th>Recipient</th>
                <th>Type</th>
                <th>Subject</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {logs.map(log => (
                <tr key={log._id} className={`adm-email-row ${selected.has(log._id) ? 'selected' : ''}`} onClick={() => setOpenId(log._id)}>
                  <td className="adm-check-cell" onClick={e => e.stopPropagation()}>
                    <input type="checkbox" checked={selected.has(log._id)} onChange={() => toggle(log._id)} aria-label={`Select email to ${log.to}`} />
                  </td>
                  <td><span className="adm-user-meta">{formatTime(log.createdAt)}</span></td>
                  <td><span className="adm-user-name">{log.to}</span></td>
                  <td><span className="adm-user-meta">{log.type}</span></td>
                  <td>{log.subject}</td>
                  <td>
                    <StatusText status={log.status} />
                    {log.openedAt && <span className="adm-user-meta">Opened</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!error && !loading && logs.length === 0 && (
        <div className="adm-empty">
          <p>{filtersActive ? 'No emails match these filters.' : 'No emails have been sent yet.'}</p>
        </div>
      )}

      {hasMore && (
        <div className="adm-load-more">
          <button className="adm-btn ghost" disabled={loading} onClick={() => load(logs[logs.length - 1]._id)}>
            {loading ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}

      {openId && (
        <SentEmailModal
          id={openId}
          route={route}
          adminFetch={adminFetch}
          onClose={() => setOpenId(null)}
          onResent={() => { load(); onChange?.() }}
          onDeleted={() => { load(); onChange?.() }}
        />
      )}
    </>
  )
}

export default EmailLogPanel
